/**
 * Vignettes 3D des bâtiments et des remontées (menu de construction, fiches),
 * comme dans les jeux de gestion : chaque modèle du pack est rendu une fois,
 * de trois quarts avant, sur fond transparent, par le renderer du jeu dans
 * une cible hors écran suréchantillonnée, puis converti en image. Le rendu
 * principal n'est pas touché : cible et couleur de fond sont rétablies juste
 * après chaque vignette, avant la lecture asynchrone des pixels.
 */
import * as THREE from 'three/webgpu';
import { signal } from '@preact/signals';
import { BUILDINGS, LIFTS, type BuildingKind, type LiftKind } from '@snowchain/shared';
import type { AssetLibrary } from './assets';

/** Images prêtes, par clé : `b:<type de bâtiment>` ou `l:<type de remontée>`. */
export const thumbs = signal<Record<string, string>>({});

const OUT_W = 256;
const OUT_H = 192;
/** Cible ×2 (512 px de large : lignes alignées sur 256 octets, exigence WebGPU). */
const RW = OUT_W * 2;
const RH = OUT_H * 2;

/** Modèle montré pour chaque type : le véhicule (siège, cabine), la gare pour le téléski. */
function jobs(): { key: string; asset: string }[] {
  const list: { key: string; asset: string }[] = [];
  for (const k of Object.keys(LIFTS) as LiftKind[]) {
    const a = LIFTS[k].assets;
    list.push({ key: `l:${k}`, asset: k === 'teleski' ? a.station : a.carrier });
  }
  for (const k of Object.keys(BUILDINGS) as BuildingKind[]) list.push({ key: `b:${k}`, asset: BUILDINGS[k].asset });
  return list;
}

let started = false;

/** Lance une fois pour toutes le rendu des vignettes (elles arrivent au fil de l'eau dans `thumbs`). */
export function ensureThumbs(renderer: THREE.WebGPURenderer, lib: AssetLibrary): void {
  if (started || lib.fallback) return;
  started = true;
  renderAll(renderer, lib).catch((e) => console.warn('[thumbs] vignettes indisponibles', e));
}

async function renderAll(renderer: THREE.WebGPURenderer, lib: AssetLibrary): Promise<void> {
  const scene = new THREE.Scene();
  const sun = new THREE.DirectionalLight(0xfff2e0, 2.7);
  const fill = new THREE.DirectionalLight(0xc4dcff, 0.8);
  scene.add(new THREE.HemisphereLight(0xeef5ff, 0x77889a, 1.35), sun, sun.target, fill, fill.target);
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 5000);
  const rt = new THREE.RenderTarget(RW, RH, { depthBuffer: true });
  rt.texture.colorSpace = THREE.SRGBColorSpace;
  const big = document.createElement('canvas');
  big.width = RW;
  big.height = RH;
  const out = document.createElement('canvas');
  out.width = OUT_W;
  out.height = OUT_H;
  const bigCtx = big.getContext('2d');
  const outCtx = out.getContext('2d');
  if (!bigCtx || !outCtx) return;
  outCtx.imageSmoothingQuality = 'high';
  const webgl = !(renderer.backend as unknown as { isWebGPUBackend?: boolean }).isWebGPUBackend;
  const box = new THREE.Box3();
  const center = new THREE.Vector3();
  const size = new THREE.Vector3();
  const p = new THREE.Vector3();
  const az = THREE.MathUtils.degToRad(36);
  const el = THREE.MathUtils.degToRad(26);
  const dir = new THREE.Vector3(Math.sin(az) * Math.cos(el), Math.sin(el), Math.cos(az) * Math.cos(el));
  const ready: Record<string, string> = {};
  for (const job of jobs()) {
    if (!lib.has(job.asset)) continue;
    const geo = lib.get(job.asset).geometries[0];
    if (!geo) continue;
    geo.computeBoundingBox();
    box.copy(geo.boundingBox as THREE.Box3);
    box.getCenter(center);
    box.getSize(size);
    const radius = Math.max(1, size.length() / 2);
    const dist = radius * 4;
    camera.position.copy(center).addScaledVector(dir, dist);
    camera.lookAt(center);
    camera.updateMatrixWorld();
    // Cadrage serré : les huit coins de la boîte dans le repère de la caméra.
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (let i = 0; i < 8; i++) {
      p.set(i & 1 ? box.max.x : box.min.x, i & 2 ? box.max.y : box.min.y, i & 4 ? box.max.z : box.min.z).applyMatrix4(camera.matrixWorldInverse);
      minX = Math.min(minX, p.x);
      maxX = Math.max(maxX, p.x);
      minY = Math.min(minY, p.y);
      maxY = Math.max(maxY, p.y);
    }
    let w = maxX - minX;
    let h = maxY - minY;
    if (w / h > OUT_W / OUT_H) h = (w * OUT_H) / OUT_W;
    else w = (h * OUT_W) / OUT_H;
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const pad = 1.1;
    camera.left = cx - (w * pad) / 2;
    camera.right = cx + (w * pad) / 2;
    camera.top = cy + (h * pad) / 2;
    camera.bottom = cy - (h * pad) / 2;
    camera.far = dist + radius * 3;
    camera.updateProjectionMatrix();
    // Soleil de face à gauche, contre-jour froid à droite.
    sun.position.copy(center).add(p.set(-0.55, 1.05, 0.95).multiplyScalar(dist));
    sun.target.position.copy(center);
    fill.position.copy(center).add(p.set(1, 0.35, -0.4).multiplyScalar(dist));
    fill.target.position.copy(center);
    sun.target.updateMatrixWorld();
    fill.target.updateMatrixWorld();
    const mesh = new THREE.Mesh(geo, lib.plain);
    scene.add(mesh);
    const prevTarget = renderer.getRenderTarget();
    const prevColor = renderer.getClearColor(new THREE.Color());
    const prevAlpha = renderer.getClearAlpha();
    renderer.setRenderTarget(rt);
    renderer.setClearColor(0x000000, 0);
    renderer.clear();
    renderer.render(scene, camera);
    renderer.setRenderTarget(prevTarget);
    renderer.setClearColor(prevColor, prevAlpha);
    scene.remove(mesh);
    const raw = (await renderer.readRenderTargetPixelsAsync(rt, 0, 0, RW, RH)) as Uint8Array;
    const pixels = new Uint8ClampedArray(RW * RH * 4);
    const row = RW * 4;
    // WebGL lit les lignes de bas en haut, WebGPU de haut en bas.
    for (let y = 0; y < RH; y++) pixels.set(raw.subarray((webgl ? RH - 1 - y : y) * row, (webgl ? RH - y : y + 1) * row), y * row);
    bigCtx.putImageData(new ImageData(pixels, RW, RH), 0, 0);
    outCtx.clearRect(0, 0, OUT_W, OUT_H);
    outCtx.drawImage(big, 0, 0, OUT_W, OUT_H);
    ready[job.key] = out.toDataURL('image/png');
    thumbs.value = { ...ready };
  }
  rt.dispose();
}
